/**
 * FormField.js
 *
 * A reusable form field component that handles different types of input fields:
 * - Text inputs
 * - Number inputs
 * - Select dropdowns
 * - Date/time inputs
 *
 * Features:
 * - Consistent styling and behavior across field types
 * - Loading states for select fields
 * - Timezone-aware datetime inputs
 * - Full-width responsive design
 * - Error handling and validation display
 */

import React from "react";
import { TextField, MenuItem, CircularProgress } from "@mui/material";

/**
 * FormField Component
 *
 * A flexible form field component that renders different types of input fields
 * based on the provided type prop.
 *
 * @param {Object} props - Component properties
 * @param {string} props.type - Field type ('text', 'number', 'select', 'datetime-local')
 * @param {string} props.name - Field name/identifier
 * @param {string} props.label - Field label
 * @param {Array} props.options - Options for select fields
 * @param {boolean} props.isLoading - Loading state for select fields
 * @param {Object} props - Additional TextField props
 */
const FormField = ({ type, name, label, options, isLoading, ...props }) => {
  switch (type) {
    case "select":
      return (
        <TextField
          select
          fullWidth
          margin="normal"
          name={name}
          label={label}
          {...props}
        >
          {isLoading ? (
            <MenuItem disabled>
              <CircularProgress size={20} /> Loading...
            </MenuItem>
          ) : (
            options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))
          )}
        </TextField>
      );
    case "datetime-local":
      return (
        <TextField
          fullWidth
          margin="normal"
          type="datetime-local"
          name={name}
          label={`${label} (${
            Intl.DateTimeFormat().resolvedOptions().timeZone
          })`}
          InputLabelProps={{
            shrink: true,
          }}
          {...props}
        />
      );
    case "number":
      return (
        <TextField
          fullWidth
          margin="normal"
          type="number"
          name={name}
          label={label}
          {...props}
        />
      );
    default:
      return (
        <TextField
          fullWidth
          margin="normal"
          type={type}
          name={name}
          label={label}
          {...props}
        />
      );
  }
};

export default FormField;
